export interface Pillar {
  name: string
  focus: string
  firstIndicator: string
  baseline: string
}

export interface PilotPhase {
  window: string
  title: string
  detail: string
  deliverables: string[]
}

/**
 * Grounded in the ARREST Agenda for Inclusive Development (AAID), Liberia's
 * National Development Plan 2025 to 2029. Figures are quoted from the plan.
 */
export const AAID_PILLARS: Pillar[] = [
  {
    name: 'Economic Transformation',
    focus: 'Investment, job creation, export growth and import substitution through industrialisation.',
    firstIndicator: 'GDP per capita against the plan target',
    baseline: 'Target US$1,050.3 with average annual growth of 5.9 per cent',
  },
  {
    name: 'Infrastructural Development',
    focus: 'Transport, logistics, energy, housing and ICT, to cut travel time and the cost of doing business.',
    firstIndicator: 'Kilometres of primary road paved and commissioned',
    baseline: 'Target a minimum of 485 km of primary roads',
  },
  {
    name: 'Rule of Law',
    focus: 'Legal frameworks for a transparent justice system that protects rights and builds public trust.',
    firstIndicator: 'Case clearance time in the courts covered by the reform',
    baseline: 'Baseline to be established in the first thirty days',
  },
  {
    name: 'Governance and Anti-Corruption',
    focus: 'A functional state, with accountability at national and sub national level.',
    firstIndicator: 'Value of flagged budget variance resolved within one quarter',
    baseline: 'Baseline to be established from the first programme budget',
  },
  {
    name: 'Environmental Sustainability',
    focus: 'Responsible natural resource management, biodiversity and climate resilience.',
    firstIndicator: 'Share of concessions reporting against agreed environmental conditions',
    baseline: 'Baseline to be established in the first thirty days',
  },
  {
    name: 'Human Capital Development',
    focus: 'Education, healthcare, sanitation, and the empowerment of women, youth, children and people with disabilities.',
    firstIndicator: 'Technical and vocational places filled, by county and by sex',
    baseline: '74.6 per cent of the population is under 35, and 41.5 per cent under 18',
  },
]

export const DELIVERY_GAPS = [
  {
    title: 'There is no single agreed baseline',
    detail:
      'Six pillars, dozens of programmes and several statistical sources. Until one figure per indicator is agreed and dated, every progress discussion becomes a discussion about the numbers themselves.',
  },
  {
    title: 'Ministry reporting arrives late and in different shapes',
    detail:
      'Returns come as documents rather than data, on different cycles and in incompatible formats. Consolidating them by hand consumes the quarter it is meant to report on.',
  },
  {
    title: 'Budget lines are not yet joined to outcomes',
    detail:
      'The plan commits to programme based budgeting linked to development outcomes. That link has to be built once, deliberately, or the budget and the plan drift apart within two cycles.',
  },
  {
    title: 'Slippage and leakage surface after the money is spent',
    detail:
      'Variance is usually found in an annual audit. By then the resource is gone. Early signals exist in the reporting already being collected, if something is reading it every week.',
  },
]

export const PILOT_PHASES: PilotPhase[] = [
  {
    window: 'Days 1 to 30',
    title: 'Baseline register',
    detail:
      'We agree one indicator set for the six pillars, one authoritative source per indicator, and one dated baseline figure. Where no figure exists we say so rather than estimating quietly.',
    deliverables: [
      'Indicator register, one owner and one source per line',
      'Dated baseline figures with confidence stated',
      'Named data gaps, with the cost of closing each',
    ],
  },
  {
    window: 'Days 31 to 60',
    title: 'Drivers, filters and the budget link',
    detail:
      'We map the plan\u2019s own drivers and filters into a cause and consequence model, then join each programme budget line to the outcome it is meant to move.',
    deliverables: [
      'Cross sectoral driver and filter map',
      'Programme to outcome linkage for the current budget',
      'Twelve leading indicators chosen for weekly reading',
    ],
  },
  {
    window: 'Days 61 to 90',
    title: 'Delivery dashboard, live',
    detail:
      'The dashboard goes live for the presidency, the ministry of finance and each sector ministry, with an assisted reading layer that flags slippage and variance for a human to judge.',
    deliverables: [
      'Delivery dashboard, by pillar, programme and county',
      'Assisted early warning on slippage and budget variance',
      'First quarterly delivery review, chaired with your team',
      'Citizen facing progress page, published from the same data',
    ],
  },
]

export const GUARDRAILS = [
  'A named official reviews every flag before it informs a decision. The system recommends, it never decides.',
  'No automated decision is made about any individual citizen, and no personal data is required to run the dashboard.',
  'Sovereign data. Records stay in country, with a mirrored copy only where the government instructs it.',
  'Every figure carries its source, its date and a stated confidence. Where evidence is thin we publish that instead of smoothing it.',
  'Assistive analysis is logged and reproducible, so any published number can be traced back to the return it came from.',
]

export const REPLICATION_STEPS = [
  {
    title: 'The instrument is the same',
    detail:
      'Every national development plan sets pillars, targets and a reporting duty. The baseline register, the outcome linkage and the delivery dashboard transfer without redesign.',
  },
  {
    title: 'Only the indicator set is local',
    detail:
      'Pillars, targets and county structures are rebuilt from each plan. This is four to six weeks of work rather than a new engagement from first principles.',
  },
  {
    title: 'Proof travels faster than a proposal',
    detail:
      'A published quarterly review from one government is the reference the next one asks for. That is why the pilot is priced and scoped to succeed publicly.',
  },
]
